import { Alert, Box, CircularProgress, Stack, Tab, Tabs, Typography } from '@mui/material'
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { routeByName, toOrderRowModel } from '@commerce/core'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'
import { OrderCard, itemCountLabel } from '../components/OrderCard'
import { Screen } from '../design'
import { ORDER_TABS, type OrderTab } from '../demo'
import { useOrders } from '../hooks/useOrders'
import { usePresentationContext } from '../hooks/usePresentationContext'
import { BottomNav } from '../components/BottomNav'

const orderDetailPath = (slug: string, sessionId: string, orderId: string): string =>
  routeByName('orderDetail')
    .path.replace(':slug', slug)
    .replace(':sessionId', sessionId)
    .replace(':orderId', orderId)

/**
 * The API status each tab asks the server for. `null` is "everything", which is
 * why the map is exhaustive over `OrderTab` rather than a lookup with a
 * fallback: a tab added to `ORDER_TABS` without a rule here is a type error,
 * not a tab that silently shows every order.
 *
 * One API status per tab, not one badge label. The badge groups `PAID` and
 * `PROCESSED` under "Confirmado" so a shopper reads one word, but the filter is
 * literal — `Confirmados` asks for `CONFIRMED` and nothing else. Grouping here
 * too would need a status list the filter does not accept.
 */
const TAB_STATUS: Record<OrderTab, string | null> = {
  Todos: null,
  Confirmados: 'CONFIRMED',
  Entregados: 'DELIVERED',
  Cancelados: 'CANCELLED',
}

const tabId = (tab: OrderTab): string => `orders-tab-${tab}`
const panelId = (tab: OrderTab): string => `orders-panel-${tab}`

/**
 * The order history, from the session's own orders.
 *
 * The list is server-filtered per tab and only the first page is loaded, so
 * "no orders with this status" is the server's answer, not a narrowing of what
 * happened to be fetched.
 *
 * Totals go through `toPriceText` rather than being printed: an amount in the
 * wrong currency, or shown at all to a customer whose session hides prices, is
 * wrong however real the number is.
 */
export const OrdersPage = () => {
  const { slug = '', sessionId = '' } = useParams()
  const navigate = useNavigate()
  const presentation = usePresentationContext()
  const [tab, setTab] = useState<OrderTab>('Todos')

  const { orders, loading, error } = useOrders(TAB_STATUS[tab] ?? undefined)

  const models = useMemo(
    () =>
      orders.map((order) => {
        const row = toOrderRowModel(order, presentation, presentation.locale)
        return {
          id: row.id,
          date: row.dateText,
          status: row.status,
          itemCountText: itemCountLabel(row.units),
          totalText: row.totalText,
          reference: row.reference,
        }
      }),
    [orders, presentation],
  )

  return (
    <Screen testId="orders-page">
      <Box sx={{ px: 2.5, pt: 1.75, pb: 1.25 }}>
        <Typography variant="h3" component="h1">
          Mis pedidos
        </Typography>
      </Box>

      {/* Real tabs, not styled divs: this gives the filter a tablist, arrow-key
          navigation between the tabs and a panel that announces which filter
          produced it. The pill look is the design's; the semantics are MUI's. */}
      <Tabs
        value={tab}
        onChange={(_event, next: OrderTab) => setTab(next)}
        variant="scrollable"
        scrollButtons={false}
        aria-label="Filtrar pedidos por estado"
        sx={{
          px: 2.5,
          pb: 1.5,
          minHeight: 34,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTabs-flexContainer': { gap: 1 },
          '& .MuiTab-root': {
            minHeight: 34,
            minWidth: 0,
            px: 1.875,
            py: 0,
            borderRadius: `${YALO_RADIUS.pill}px`,
            border: `1px solid ${YALO_SEMANTIC.borderDefault}`,
            bgcolor: YALO_SEMANTIC.bgSurface,
            color: YALO_SEMANTIC.fg2,
            fontSize: 13,
            fontWeight: 600,
          },
          '& .MuiTab-root.Mui-selected': {
            bgcolor: 'primary.main',
            borderColor: 'primary.main',
            color: 'primary.contrastText',
          },
        }}
      >
        {ORDER_TABS.map((entry) => (
          <Tab
            key={entry}
            value={entry}
            label={entry}
            id={tabId(entry)}
            aria-controls={panelId(entry)}
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={panelId(tab)}
        aria-labelledby={tabId(tab)}
        sx={{ px: 2.5, pb: 4 }}
      >
        {/* The error is shown instead of the list, not beside it: an empty
            list under a failed fetch reads as "you have no orders", which is a
            different and worse claim than "we could not load them". */}
        {error ? (
          <Alert severity="error" sx={{ my: 2 }}>
            No pudimos cargar tus pedidos. {error.message}
          </Alert>
        ) : loading && models.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : models.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 3 }}>
            {tab === 'Todos'
              ? 'Aún no tienes pedidos.'
              : 'No tienes pedidos con este estado.'}
          </Typography>
        ) : (
          <Stack spacing={1.25}>
            {models.map((model) => (
              <OrderCard
                key={model.id}
                model={model}
                onOpen={(orderId) => navigate(orderDetailPath(slug, sessionId, orderId))}
              />
            ))}
          </Stack>
        )}
      </Box>

      <BottomNav slug={slug} sessionId={sessionId} />
    </Screen>
  )
}
